import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../components";
import { useToast } from "../../../../components/toast-container";
import FormHeader from "../../../../components/form/FormHeader";
import EventEditor, { type EventEditorSubmitValues } from "../components/event-form/EventEditor";
import type { CreateEventForAdminRequest } from "../types/event.types";
import { useCreateEvent } from "../hooks";
import { buildEventDateTimeFields } from "../../../../lib/date-time";
import LeftArrow from "../../../../assets/icons/left-arrow-gray-small.svg";
import { CalendarPlus } from "lucide-react";
import "../assets/css/index.css";

export default function EventCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createEvent, loading: isSubmitting } = useCreateEvent();
  const { addToast } = useToast();
  const formId = "event-create-form";

  const handleCreateEvent = async (values: EventEditorSubmitValues) => {
    const { startTime, endTime, eventDateTimeOffset, checkInTimeOffset } = buildEventDateTimeFields(
      values.eventDate,
      values.start,
      values.end,
      values.checkInTime,
      values.timezone,
    );
    const submitRequest: CreateEventForAdminRequest = {
      title: values.name,
      description: values.description,
      latitude: Number(values.lat),
      longitude: Number(values.lon),
      radius: Number(values.radius),
      event_date: eventDateTimeOffset,
      start_time: startTime,
      end_time: endTime,
      check_in_time: checkInTimeOffset || undefined,
      guest_attendance_verification: values.guestAttendanceVerification || undefined,
      location: values.location,
      points: values.points ? Number(values.points) : undefined,
      event_image_file: values.image ?? undefined,
      timezone: values.timezone,
      category_id: values.categoryId || undefined,
      sub_category_ids: values.subCategoryIds,
    };

    await createEvent(submitRequest);
    addToast({
      type: "success",
      title: "Success!",
      message: "Event has been created successfully",
    });
    navigate("/merchant/events");
  };

  return (
    <div className="flex flex-col bg-slate-50/70 dark:bg-slate-950">
      <FormHeader
        onBack={() => navigate("/merchant/events")}
        title={t("event.create_event")}
        subtitle=""
        backLabel={t("common.back")}
        backIcon={
          <img
            src={LeftArrow}
            alt="back"
            className="dark:invert"
          />
        }
        className="border-b-0 px-2 shadow-sm"
        actions={
          <div className="flex items-center justify-end gap-2">
            <Button
              type="submit"
              form={formId}
              disabled={isSubmitting}
              className="bg-emerald-500 text-white shadow-sm hover:bg-emerald-600"
            >
              <CalendarPlus className="h-4 w-4" />
              {isSubmitting ? t("event.creating") : t("event.create")}
            </Button>
          </div>
        }
      />

      <EventEditor
        mode="create"
        formId={formId}
        onSubmit={handleCreateEvent}
      />
    </div>
  );
}
